import React, { useState, useRef, useMemo } from "react";
import { toPng } from "html-to-image";
import { FaShieldAlt } from "react-icons/fa";

const DEFAULT_BG =
  "https://images.unsplash.com/photo-1518605368461-1ee7c532066d?q=80&w=1080&auto=format&fit=crop";

const ExportBracketsModal = ({
  isOpen,
  onClose,
  phases,
  teams,
  subcategory,
}) => {
  const exportRef = useRef(null);

  const [topLogo, setTopLogo] = useState(
    localStorage.getItem("exportTopLogo") || "",
  );
  const [bottomLogo, setBottomLogo] = useState(
    localStorage.getItem("exportBottomLogo") || "",
  );
  const [bgImage, setBgImage] = useState(
    localStorage.getItem("exportBgImage") || DEFAULT_BG,
  );
  const [isExporting, setIsExporting] = useState(false);
  const [selectedPhases, setSelectedPhases] = useState([]);

  const togglePhase = (phaseName) => {
    setSelectedPhases((prev) =>
      prev.includes(phaseName)
        ? prev.filter((p) => p !== phaseName)
        : [...prev, phaseName],
    );
  };

  const selectAll = () => setSelectedPhases(phases.map((p) => p.name));
  const clearSelection = () => setSelectedPhases([]);

  const selectedPhaseObjects = useMemo(
    () => phases.filter((p) => selectedPhases.includes(p.name)),
    [phases, selectedPhases],
  );

  const isBracketMode = selectedPhaseObjects.length > 1;

  const BRACKET = {
    MATCH_WIDTH: 220,
    MATCH_HEIGHT: 70,
    H_GAP: 60,
    V_GAP: 30,
    PHASE_HEADER_HEIGHT: 36,
  };

  const bracketLayout = useMemo(() => {
    if (!isBracketMode || selectedPhaseObjects.length === 0) return null;

    const rounds = selectedPhaseObjects.map((phase) => ({
      name: phase.name,
      matches: phase.matches,
      numMatches: phase.matches.length,
    }));

    const maxMatches = rounds[0]?.numMatches || 0;
    const matchBlockHeight =
      maxMatches * (BRACKET.MATCH_HEIGHT + BRACKET.V_GAP) - BRACKET.V_GAP;
    const totalHeight = BRACKET.PHASE_HEADER_HEIGHT + matchBlockHeight;

    const roundPositions = rounds.map((round, rIdx) => ({
      ...round,
      x: rIdx * (BRACKET.MATCH_WIDTH + BRACKET.H_GAP),
    }));

    const positionedMatches = roundPositions.map((round) => {
      const { numMatches, x } = round;
      const availableHeight =
        numMatches * BRACKET.MATCH_HEIGHT + (numMatches - 1) * BRACKET.V_GAP;
      const startY =
        BRACKET.PHASE_HEADER_HEIGHT + (matchBlockHeight - availableHeight) / 2;

      return round.matches.map((match, mIdx) => ({
        ...match,
        x,
        y: startY + mIdx * (BRACKET.MATCH_HEIGHT + BRACKET.V_GAP),
      }));
    });

    // Líneas de conexión
    const lines = [];
    for (let r = 0; r < positionedMatches.length - 1; r++) {
      const currentRound = positionedMatches[r];
      const nextRound = positionedMatches[r + 1];
      for (let i = 0; i < nextRound.length; i++) {
        const child = nextRound[i];
        const parent1 = currentRound[i * 2];
        const parent2 = currentRound[i * 2 + 1];
        if (parent1 && parent2) {
          const xStart = parent1.x + BRACKET.MATCH_WIDTH;
          const yStart = parent1.y + BRACKET.MATCH_HEIGHT / 2;
          const xMid = xStart + BRACKET.H_GAP / 2;
          const yChild = child.y + BRACKET.MATCH_HEIGHT / 2;

          lines.push({
            path: `M ${xStart} ${yStart} L ${xMid} ${yStart} L ${xMid} ${yChild} L ${child.x} ${yChild}`,
          });
          lines.push({
            path: `M ${xStart} ${parent2.y + BRACKET.MATCH_HEIGHT / 2} L ${xMid} ${
              parent2.y + BRACKET.MATCH_HEIGHT / 2
            } L ${xMid} ${yChild}`,
          });
        }
      }
    }

    return {
      totalWidth:
        roundPositions.length * (BRACKET.MATCH_WIDTH + BRACKET.H_GAP) -
        BRACKET.H_GAP,
      totalHeight,
      matches: positionedMatches.flat(),
      lines,
      rounds: roundPositions,
    };
  }, [selectedPhaseObjects, isBracketMode]);

  const getSubtitle = () => {
    let parts = [];
    if (subcategory && typeof subcategory !== "string") {
      if (subcategory.number) parts.push(`Categoría ${subcategory.number}`);
      if (subcategory.group_name) parts.push(subcategory.group_name);
      if (parts.length === 0 && subcategory.display)
        parts.push(subcategory.display);
      else if (parts.length === 0 && subcategory.name)
        parts.push(subcategory.name);
    } else if (typeof subcategory === "string" && subcategory) {
      parts.push(subcategory);
    }
    return parts.join(" - ");
  };

  const subtitle = getSubtitle();

  const handleImageUpload = (e, setFunction, storageKey) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1500000) {
        alert(
          "La imagen es muy grande. Por favor sube una imagen menor a 1.5MB",
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFunction(reader.result);
        localStorage.setItem(storageKey, reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExport = async () => {
    if (!exportRef.current) return;
    setIsExporting(true);
    try {
      const dataUrl = await toPng(exportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "transparent",
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `Bracket_${subtitle || "Torneo"}.png`;
      link.click();
    } catch (error) {
      alert("Hubo un error al generar la imagen.");
    } finally {
      setIsExporting(false);
    }
  };

  // ========== MATCH CARD CON CENTRADO VERTICAL ==========
  const MatchCard = ({ match, style = {} }) => {
    const team1Name =
      match.team1_name_db ||
      teams.find((t) => t.team_id === match.team1_id)?.team_name ||
      "TBD";
    const team2Name =
      match.team2_name_db ||
      teams.find((t) => t.team_id === match.team2_id)?.team_name ||
      "TBD";
    const team1Logo =
      match.team1_logo_db ||
      teams.find((t) => t.team_id === match.team1_id)?.picture_url;
    const team2Logo =
      match.team2_logo_db ||
      teams.find((t) => t.team_id === match.team2_id)?.picture_url;

    return (
      // MODIFICADO: agregué flex, justify-center y gap-1 para centrar verticalmente
      <div
        className="bg-white/10 border border-white/20 rounded-lg p-2 text-white text-xs flex flex-col justify-center gap-1"
        style={{
          width: BRACKET.MATCH_WIDTH,
          minHeight: BRACKET.MATCH_HEIGHT,
          ...style,
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {team1Logo ? (
              <img
                src={team1Logo}
                className="w-5 h-5 rounded-full object-cover"
                alt=""
              />
            ) : (
              <FaShieldAlt size={12} className="text-white/70" />
            )}
            <span className="truncate font-medium">{team1Name}</span>
          </div>
          <span className="font-bold">{match.score_team1 ?? "-"}</span>
        </div>
        {/* MODIFICADO: quité mt-1 porque el gap del contenedor ya maneja la separación */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {team2Logo ? (
              <img
                src={team2Logo}
                className="w-5 h-5 rounded-full object-cover"
                alt=""
              />
            ) : (
              <FaShieldAlt size={12} className="text-white/70" />
            )}
            <span className="truncate font-medium">{team2Name}</span>
          </div>
          <span className="font-bold">{match.score_team2 ?? "-"}</span>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col md:flex-row shadow-2xl">
        {/* Panel izquierdo */}
        <div className="w-full md:w-1/3 p-6 bg-gray-50 overflow-y-auto border-r border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Exportar Bracket
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-red-500 font-bold text-xl"
            >
              &times;
            </button>
          </div>

          <div className="space-y-2 mb-6">
            <label className="block text-sm font-semibold text-black">
              Selecciona las fases
            </label>
            <div className="flex gap-2 mb-2">
              <button
                onClick={selectAll}
                className="text-xs text-gray-900 bg-gray-200 px-2 py-1 rounded hover:bg-gray-300"
              >
                Todas
              </button>
              <button
                onClick={clearSelection}
                className="text-xs text-gray-900 bg-gray-200 px-2 py-1 rounded hover:bg-gray-300"
              >
                Ninguna
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {phases.map((phase) => (
                <label
                  key={phase.id}
                  className="flex items-center text-gray-900 gap-2 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedPhases.includes(phase.name)}
                    onChange={() => togglePhase(phase.name)}
                    className="accent-blue-600"
                  />
                  {phase.name} ({phase.matches.length})
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Superior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setTopLogo, "exportTopLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Imagen de Fondo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBgImage, "exportBgImage")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <button
                onClick={() => {
                  setBgImage(DEFAULT_BG);
                  localStorage.removeItem("exportBgImage");
                }}
                className="mt-2 text-xs text-red-500 hover:underline"
              >
                Restaurar fondo por defecto
              </button>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Inferior
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(e, setBottomLogo, "exportBottomLogo")
                }
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <button
              onClick={handleExport}
              disabled={isExporting || selectedPhases.length === 0}
              className={`w-full py-3 rounded-xl font-bold text-white transition-colors ${
                isExporting || selectedPhases.length === 0
                  ? "bg-blue-300"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isExporting ? "Generando..." : "Descargar Imagen"}
            </button>
          </div>
        </div>

        {/* Vista previa */}
        <div className="w-full md:w-2/3 p-6 bg-gray-200 overflow-auto flex items-center justify-center">
          <div
            ref={exportRef}
            className="relative bg-blue-900 text-white overflow-hidden shadow-xl"
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              fontFamily: "'Inter', sans-serif",
              padding: "40px 20px",
              ...(bracketLayout
                ? {
                    width: bracketLayout.totalWidth + 80,
                    minHeight: bracketLayout.totalHeight + 160,
                  }
                : { width: 360, minHeight: 200 }),
            }}
          >
            {/* Cabecera general */}
            <div className="flex flex-col items-center mb-8">
              {topLogo && (
                <img
                  src={topLogo}
                  alt="Top Logo"
                  className="h-16 object-contain mb-4"
                />
              )}
              <h1
                className="text-5xl font-black tracking-tighter uppercase m-0 leading-none text-center"
                style={{ fontFamily: "Impact, sans-serif" }}
              >
                BRACKET
              </h1>
              {subtitle && (
                <h2 className="text-lg font-bold tracking-widest uppercase mt-2 text-blue-200 text-center">
                  {subtitle}
                </h2>
              )}
            </div>

            {/* Contenido del bracket */}
            <div className="flex justify-center">
              {selectedPhaseObjects.length === 0 ? (
                <p className="text-white/70 text-sm">
                  Seleccioná al menos una fase para previsualizar
                </p>
              ) : !isBracketMode ? (
                /* Modo lista simple (UNA FASE) - CENTRADO HORIZONTAL */
                // NUEVO: cambié w-full por mx-auto para centrar correctamente
                <div className="mx-auto max-w-[320px] space-y-3">
                  <h3 className="text-center text-lg font-bold uppercase tracking-wide text-blue-200">
                    {selectedPhaseObjects[0].name}
                  </h3>
                  {selectedPhaseObjects[0].matches.map((match, idx) => (
                    <MatchCard key={idx} match={match} />
                  ))}
                </div>
              ) : (
                /* Modo bracket (MÚLTIPLES FASES) */
                <div
                  style={{
                    position: "relative",
                    width: bracketLayout.totalWidth,
                    height: bracketLayout.totalHeight,
                  }}
                >
                  {/* Líneas de conexión */}
                  <svg
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                    }}
                  >
                    {bracketLayout.lines.map((line, i) => (
                      <path
                        key={i}
                        d={line.path}
                        stroke="white"
                        strokeOpacity={0.5}
                        strokeWidth={2}
                        fill="none"
                      />
                    ))}
                  </svg>

                  {/* Nombres de las fases */}
                  {bracketLayout.rounds.map((round) => (
                    <div
                      key={round.name}
                      className="absolute text-center text-sm font-bold uppercase text-blue-200 tracking-wider"
                      style={{
                        left: round.x,
                        top: 0,
                        width: BRACKET.MATCH_WIDTH,
                        height: BRACKET.PHASE_HEADER_HEIGHT,
                        lineHeight: `${BRACKET.PHASE_HEADER_HEIGHT}px`,
                      }}
                    >
                      {round.name}
                    </div>
                  ))}

                  {/* Tarjetas de partidos */}
                  {bracketLayout.matches.map((match, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: "absolute",
                        left: match.x,
                        top: match.y,
                      }}
                    >
                      <MatchCard match={match} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pie de página */}
            <div className="mt-8 pt-6 flex justify-center items-center">
              {bottomLogo && (
                <img
                  src={bottomLogo}
                  alt="Bottom Logo"
                  className="h-14 object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportBracketsModal;
